import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ListCellDefDirective } from './list-cell-def.directive';
import { ListModuleColumnDef, ListModuleSortState } from './list-module-table.types';
import { ListModuleTableStatesComponent } from './list-module-table-states.component';

@Component({
  selector: 'app-list-module-table',
  standalone: true,
  imports: [CommonModule, ListModuleTableStatesComponent],
  templateUrl: './list-module-table.component.html',
})
export class ListModuleTableComponent {
  /** `surface` = manage-user table classes; `compact` = manage-roles style. */
  @Input() variant: 'surface' | 'compact' = 'surface';

  @Input() columns: ListModuleColumnDef[] = [];
  @Input() rows: unknown[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No records match your filters.';
  @Input() rowTrackKey: keyof Record<string, unknown> | null = null;
  /** When set, overrides {@link rowTrackKey} for `@for` track expressions (e.g. id + status). */
  @Input() rowTrackFn: ((row: unknown, index: number) => unknown) | null = null;

  @Input() sortColumn = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';

  @Output() sortChange = new EventEmitter<ListModuleSortState>();

  @ContentChildren(ListCellDefDirective) cellDefs!: QueryList<ListCellDefDirective>;

  get visibleColumns(): ListModuleColumnDef[] {
    return this.columns.filter((c) => c.visible !== false);
  }

  get colspan(): number {
    return Math.max(this.visibleColumns.length, 1);
  }

  get tableClass(): string {
    return this.variant === 'surface' ? 'table table-hover table-surface' : 'w-full text-sm';
  }

  get headerRowClass(): string {
    return this.variant === 'compact'
      ? 'border-b border-gray-100 dark:border-[#191e3a] bg-gray-50 dark:bg-[#0e1726]'
      : '';
  }

  cellTemplate(columnId: string): TemplateRef<unknown> | null {
    return this.cellDefs?.find((d) => d.columnId === columnId)?.template ?? null;
  }

  headerAlignClass(align?: string): string {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-end text-right';
    return 'text-left';
  }

  cellAlignClass(align?: string): string {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-end';
    return '';
  }

  thScopeClass(col: ListModuleColumnDef): string {
    const base =
      this.variant === 'compact'
        ? 'px-4 py-3 text-xs font-normal text-gray-500 dark:text-gray-400'
        : '';
    return `${base} ${this.headerAlignClass(col.align)} ${col.headerClass ?? ''}`.trim();
  }

  tdClass(col: ListModuleColumnDef): string {
    const base = this.variant === 'compact' ? 'px-4 py-4' : '';
    return `${base} ${this.cellAlignClass(col.align)} ${col.cellClass ?? ''}`.trim();
  }

  sortIndicator(col: ListModuleColumnDef): 'asc' | 'desc' | null {
    const key = col.sortKey ?? col.id;
    if (!col.sortable || this.sortColumn !== key) return null;
    return this.sortDirection;
  }

  onSort(col: ListModuleColumnDef): void {
    if (!col.sortable) return;
    const key = col.sortKey ?? col.id;
    const nextDir: 'asc' | 'desc' =
      this.sortColumn === key && this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sortChange.emit({ column: key, direction: nextDir });
  }

  rowContext(row: unknown, index: number): { $implicit: unknown; row: unknown; index: number } {
    return { $implicit: row, row, index };
  }

  trackRow(index: number, row: unknown): unknown {
    if (this.rowTrackFn) {
      return this.rowTrackFn(row, index);
    }
    if (this.rowTrackKey && row && typeof row === 'object') {
      return (row as Record<string, unknown>)[this.rowTrackKey] ?? index;
    }
    return index;
  }
}
